/**
 * Entity-abstraction layers — the extensible spine for the Witness Workbench
 * (plans/WITNESS_WORKBENCH_PLAN.md §1). A witness card is NOT a fixed type: it's
 * an Entity = a base + a stack of layers folded in, over the GraphRAG fragments.
 * Its abstraction level (fragment → partial → Person) is DERIVED from which
 * layers are present, never declared.
 *
 * Each layer registers how to DETECT it on an entity and how to BADGE it. The
 * index card and the Workbench both render from this list — so adding an
 * enrichment later (relationships, credibility, timeline, …) is ONE entry here,
 * not card surgery. Same pattern as `inventoryColumns` / `witnessFilterAxes`.
 *
 * A layer: { key, detect(w) -> bool, badge(w) -> {glyph?, label, cls},
 *            weight?: number }  // weight feeds the derived abstraction level
 */
import { formatRole, formatImportance } from './witnessEvidence';

export const WITNESS_LAYERS = [
  {
    key: 'status',
    detect: () => true, // every card has a status
    // Status vocabulary, not role vocabulary: "Witness" here collided with the
    // role chips (a card could read "● Witness · Victim"). Status says whether
    // the person is on the roster; the role chip says what they are.
    badge: (w) => (w.deferred
      ? { glyph: '🕓', label: 'Deferred', cls: 'deferred' }
      : w.review_state === 'accepted'
        ? { glyph: '●', label: 'On roster', cls: 'witness', tip: 'Accepted onto the case roster' }
        : { glyph: '○', label: 'Suggested', cls: 'suggested', tip: 'Found in the documents — accept, dismiss, or defer' }),
  },
  {
    key: 'composition',
    detect: (w) => (w.member_entity_ids?.length || 0) > 1,
    badge: (w) => ({ glyph: '⊕', label: `in ${w.member_entity_ids.length} documents`, cls: 'compose' }),
    weight: 2,
  },
  {
    key: 'role',
    // Show a real role only — skip the "Unnamed" placeholder (no signal).
    detect: (w) => { const r = formatRole(w); return !!r && r !== 'Unnamed'; },
    badge: (w) => ({ label: formatRole(w), cls: 'role' }),
    weight: 1,
  },
  {
    key: 'importance',
    // Only the non-default bands carry signal — don't badge "Standard".
    detect: (w) => !!w.importance && w.importance !== 'standard',
    badge: (w) => ({ label: formatImportance(w.importance), cls: `imp-${w.importance}` }),
    weight: 1,
  },
  {
    key: 'evidence',
    detect: (w) => (w.evidence_count || 0) > 0,
    badge: (w) => ({
      glyph: '📄',
      label: `${w.evidence_count}`,
      cls: 'evidence',
      tip: `${w.evidence_count} extracted statement${w.evidence_count === 1 ? '' : 's'} attributed to this person`,
    }),
    weight: 1,
  },
  {
    key: 'dup',
    detect: (w) => !!w.possible_duplicate_of?.witness_id,
    badge: () => ({ glyph: '⚠', label: 'possible match', cls: 'dup' }),
  },
  {
    key: 'health',
    // Roster-lint findings (deterministic card-health checks). `lint_count`
    // is enriched onto rows from GET /witnesses/roster-lint at render time.
    detect: (w) => (w.lint_count || 0) > 0,
    badge: (w) => ({ glyph: '⚕', label: `${w.lint_count} to review`, cls: 'health' }),
  },
];

// The badges present on a card, in registry order — what the index renders.
export function entityBadges(witness) {
  return WITNESS_LAYERS
    .filter((l) => l.detect(witness))
    .map((l) => ({ key: l.key, ...l.badge(witness) }));
}

// Derived abstraction label (NOT declared) — a coarse position on the ladder
// from the layers/weights present. Shown in the Workbench header.
export function entityAbstraction(witness) {
  const score = WITNESS_LAYERS.reduce(
    (n, l) => n + (l.detect(witness) ? (l.weight || 0) : 0), 0,
  );
  if (score >= 3) return 'Person';
  if (score >= 1) return 'Partial';
  return 'Fragment';
}
